/**
 * @description Layout principal para modo interno com drawer e header
 * @module InternalLayout
 * @since Fevereiro 2024
 */

import React, { useState } from "react";

import {
  Box,
  Drawer,
  Divider,
  useTheme,
  CircularProgress,
  useMediaQuery,
  Dialog,
} from "@mui/material";
import { } from "@mui/icons-material";

import { useSessionContext } from "@/contexts/SessionContext";

import { useSidebarNavigation } from "@/contexts/SidebarNavigationContext";

import { ViewSwitcher } from "./components/Drawer/ViewSwitcher";

import { currentFeatures } from "@/configs/Settings";

import { MobileBarNavigation } from "./components/MobileBarNavigation";
import { MainContent } from "./components/MainContent";

import { VersionViewer } from "./components/Drawer/VersionViewer";

import { LayoutPrivateHeaderContent } from "./components/Header";
import { HeaderCollapseProvider, useHeaderCollapse } from "./components/Header/HeaderCollapseContext";
import { SideBarMainContent } from "./components/Drawer/SideBarMainContent";
import { ChatLuxorProviderGate } from "@/components/common/ChatLuxor";

export const drawerWidth = 280;

interface PrivateMainLayoutProps {
  children: React.ReactNode;
  noSidebar?: boolean;
  dashboard?: boolean;
  noPadding?: boolean;
}

const PrivateMainLayout: React.FC<PrivateMainLayoutProps> = (props) => {
  const { loading } = useSessionContext();

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <HeaderCollapseProvider>
      <ChatLuxorProviderGate>
        <PrivateMainLayoutBody {...props} />
      </ChatLuxorProviderGate>
    </HeaderCollapseProvider>
  );
};

const PrivateMainLayoutBody: React.FC<PrivateMainLayoutProps> = ({
  children,
  noSidebar,
  noPadding,
}) => {
  const { roles } = useSessionContext();
  const { isSidebarExpanded: open, setIsSidebarExpanded: setOpen } =
    useSidebarNavigation();
  const { mobileHeaderSpacerHeight } = useHeaderCollapse();

  const themes = useTheme();
  const isMediumScreen = useMediaQuery(themes.breakpoints.up("md"));
  const isDesktopScreen = useMediaQuery(themes.breakpoints.up("lg"));

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  const [avatarMenuAnchorEl, setAvatarMenuAnchorEl] =
    useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAvatarMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAvatarMenuAnchorEl(null);
  };

  const headerNode = (
    <Box sx={{ flexShrink: 0 }}>
      <LayoutPrivateHeaderContent
        isMediumScreen={isMediumScreen}
        handleDrawerToggle={handleDrawerToggle}
        avatarMenuAnchorEl={avatarMenuAnchorEl}
        handleMenuOpen={handleMenuOpen}
        handleMenuClose={handleMenuClose}
        isSidebarOpen={open}
      />
    </Box>
  );

  const content = (
    <>
      {!isMediumScreen && headerNode}

      {isMediumScreen && !noSidebar && !currentFeatures.register && (
        <Drawer
          onClose={handleDrawerToggle}
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxSizing: "border-box",
            },
          }}
          variant={isDesktopScreen ? "persistent" : "temporary"}
          anchor="left"
          open={open}
          ModalProps={!isDesktopScreen ? { keepMounted: true } : undefined}
        >
          <Divider />
          <SideBarMainContent isMediumScreen={isMediumScreen} />

          {roles !== undefined &&
            roles?.length > 0 &&
            !currentFeatures.register && <ViewSwitcher isDrawerOpen={open} />}

          <VersionViewer />
        </Drawer>
      )}

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          bgcolor: "#F4F6F8",
        }}
      >
        {isMediumScreen && headerNode}

        <MainContent
          open={open}
          sx={{
            flex: 1,
            minHeight: 0,
            p: noPadding ? 0 : 2,
            pt: mobileHeaderSpacerHeight
              ? `calc(${mobileHeaderSpacerHeight} + ${noPadding ? 0 : 16}px)`
              : undefined,
            pb: !isMediumScreen ? "110px !important" : undefined,
            bgcolor: "transparent",
          }}
          isMediumScreen={isMediumScreen}
          shouldShiftForDrawer={isDesktopScreen}
        >
          {children}
          {!isMediumScreen && <MobileBarNavigation />}
        </MainContent>
      </Box>
    </>
  );

  if (!isMediumScreen) {
    return (
      <Dialog
        fullScreen
        open
        sx={{
          "& .MuiDialog-paper": {
            bgcolor: "#F4F6F8",
            height: "100%",
            maxHeight: "100dvh",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          {content}
        </Box>
      </Dialog>
    );
  }

  return (
    <Box sx={{ display: "flex", height: "100vh", overflow: "hidden" }}>
      {content}
    </Box>
  );
};
export { PrivateMainLayout };
